// Gemeinsame Bausteine für den Support — Kundenseite und Inhaberbereich.
//
// Beide Seiten zeigen denselben Verlauf, nur gespiegelt: die eigenen
// Nachrichten rechts, die der Gegenseite links. Beschriftungen kommen von
// außen, weil die Kundenseite übersetzt ist und der Inhaberbereich deutsch.
import { useState } from 'react';
import { token } from '../../../gemeinsam/lib/api';
import { dateiHochladen, groesseLesbar, type Anhang } from '../../firma/lib/dateien';
import { datumZeit } from '../../../gemeinsam/lib/daten';
import Icon from '../../../gemeinsam/komponenten/Icon';
import { Spinner } from '../../../gemeinsam/komponenten/Basis';

export type SupportAnhang = { id: string; name: string; typ: string; groesse: number };
export type SupportNachricht = {
  id: string; von: 'kunde' | 'support'; name: string; text: string; zeit: string; anhaenge: SupportAnhang[];
};

/**
 * Lädt einen Anhang über die Anfrage herunter. Ein einfacher Link ginge
 * nicht: die Datei liegt hinter der Anmeldung, und ein <a href> schickt
 * keinen Authorization-Header mit.
 */
async function anhangLaden(pfad: string, name: string) {
  const antwort = await fetch(pfad, { headers: { Authorization: `Bearer ${token() ?? ''}` } });
  if (!antwort.ok) throw new Error(String(antwort.status));
  const url = URL.createObjectURL(await antwort.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export function SupportVerlauf({ nachrichten, sicht, anhangPfad, beschriftung }: {
  nachrichten: SupportNachricht[];
  /** Wer schaut — dessen Nachrichten stehen rechts. */
  sicht: 'kunde' | 'support';
  anhangPfad: (dateiId: string) => string;
  beschriftung: { eigene: string; fremde: string };
}) {
  const [laedt, setLaedt] = useState<string | null>(null);

  return (
    <ol className="space-y-4">
      {nachrichten.map(n => {
        const eigen = n.von === sicht;
        return (
          <li key={n.id} className={`flex ${eigen ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${eigen ? 'items-end' : 'items-start'} flex flex-col`}>
              <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {eigen ? beschriftung.eigene : (n.von === 'support' ? beschriftung.fremde : n.name)}
                </span>
                {' · '}{datumZeit(n.zeit)}
              </p>
              <div className={`whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${eigen
                ? 'rounded-br-sm bg-marke-600 text-white'
                : 'rounded-bl-sm bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'}`}>
                {n.text}
              </div>
              {n.anhaenge.length > 0 && (
                <div className={`mt-1.5 flex flex-wrap gap-1.5 ${eigen ? 'justify-end' : ''}`}>
                  {n.anhaenge.map(a => (
                    <button key={a.id} type="button"
                            onClick={async () => {
                              setLaedt(a.id);
                              try { await anhangLaden(anhangPfad(a.id), a.name); } catch { /* still */ }
                              finally { setLaedt(null); }
                            }}
                            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
                      {laedt === a.id ? <Spinner klein /> : <Icon name="anhang" klasse="h-3.5 w-3.5" />}
                      <span className="max-w-[12rem] truncate">{a.name}</span>
                      {a.groesse > 0 && <span className="text-slate-400">{groesseLesbar(a.groesse)}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Dateien auswählen und hochladen — höchstens fünf. */
export function AnhangWahl({ anhaenge, setAnhaenge, beschriftung, beiFehler }: {
  anhaenge: Anhang[];
  setAnhaenge: (a: Anhang[]) => void;
  beschriftung: string;
  beiFehler: (text: string) => void;
}) {
  const [laedt, setLaedt] = useState(false);

  async function nehmen(liste: FileList | null) {
    if (!liste?.length) return;
    setLaedt(true);
    try {
      const neu: Anhang[] = [];
      for (const d of Array.from(liste).slice(0, 5 - anhaenge.length)) neu.push(await dateiHochladen(d));
      setAnhaenge([...anhaenge, ...neu].slice(0, 5));
    } catch (e) {
      beiFehler(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally { setLaedt(false); }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {anhaenge.map(a => (
        <span key={a.id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs dark:border-slate-700 dark:bg-slate-800">
          <Icon name="anhang" klasse="h-3.5 w-3.5" />
          <span className="max-w-[10rem] truncate">{a.name}</span>
          <button type="button" aria-label="×" onClick={() => setAnhaenge(anhaenge.filter(x => x.id !== a.id))}
                  className="text-slate-400 hover:text-red-600">×</button>
        </span>
      ))}
      {anhaenge.length < 5 && (
        <label className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
          {laedt ? <Spinner klein /> : <Icon name="anhang" klasse="h-3.5 w-3.5" />}
          {beschriftung}
          <input type="file" multiple className="hidden" onChange={e => { void nehmen(e.target.files); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );
}
